/**
 * O selo do miolo de Finanças: a situação do lançamento e os padrões do
 * favorecido.
 *
 * Papel: reproduz `.badge` e as classes `.bg-*` (l. 96–103) — mono 10 px 500,
 * `2px 8px`, cápsula, fundo da cor a 15 % —, mais o selo azul-claro de
 * "Lançado", que a origem escreve por `style` (l. 3106).
 *
 * Como usar: `cor` escolhe o par de fundo e texto; o `ouro` tem o texto em
 * latão escuro, como o `.bg-gold` da origem. Com `onClick`, o selo vira um
 * `<button>` do mesmo desenho, com a seta do ponteiro — é o selo de situação
 * que troca a provisão na tabela de lançamentos. Sem `onClick` ele é texto, e
 * quem só tem `ver` recebe o selo assim.
 */
import { type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

/** As oito cores de selo da origem. */
export type CorDoSelo =
  'verde' | 'vermelho' | 'amarelo' | 'azul' | 'cinza' | 'laranja' | 'ouro' | 'lancado';

/** O fundo e o texto de cada cor. */
const CLASSES_DA_COR: Readonly<Record<CorDoSelo, string>> = {
  verde: 'bg-sucesso/15 text-sucesso',
  vermelho: 'bg-perigo/15 text-perigo',
  amarelo: 'bg-emb-amarelo/15 text-emb-amarelo',
  azul: 'bg-emb-azul/15 text-emb-azul',
  cinza: 'bg-emb-cinza-fundo text-emb-cinza-texto',
  laranja: 'bg-aviso/15 text-aviso',
  ouro: 'bg-brass/15 text-brass-light',
  lancado: 'bg-emb-lancado-fundo text-emb-lancado-texto',
};

/** O que todo selo tem. */
const CLASSE_DO_SELO =
  'inline-flex items-center gap-[3px] rounded-[20px] border-0 px-2 py-0.5 font-mono text-[10px] font-medium whitespace-nowrap';

interface SeloProps {
  cor: CorDoSelo;
  children: ReactNode;
  /** Torna o selo clicável. */
  onClick?: () => void;
  /** A dica do selo clicável, como o `title` da origem. */
  title?: string;
  /** Classes de quem chama, como o `font-size:9px` das transações do Fechamento. */
  className?: string;
}

/**
 * Um selo.
 *
 * @param cor - O par de fundo e texto.
 * @param children - O texto e, quando há, o ícone.
 * @param onClick - Quando presente, o selo é um botão.
 * @param title - A dica sob o ponteiro.
 * @param className - Classes de quem chama, aplicadas por último.
 * @returns O selo, como `<span>` ou como `<button>`.
 */
export function Selo({ cor, children, onClick, title, className }: SeloProps) {
  const classes = cn(CLASSE_DO_SELO, CLASSES_DA_COR[cor], className);
  if (onClick === undefined) {
    return (
      <span className={classes} title={title}>
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={cn(classes, 'cursor-pointer')}
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
